import { useMemo } from 'react'

import { IColumnsType } from 'pared/components/basicUi/table'

import { IAccountingSalesReportData } from './useAccountingSalesReportDatasource'

const formatCurrency = (value?: number): string => {
  if (value === undefined || value === null) return ''
  return `$${value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
}

const formatPercentage = (value?: number): string => {
  if (value === undefined || value === null) return ''
  return `${value.toFixed(2)}%`
}

// Style functions for different column types
const getBlueBackgroundStyle = () => {
  return `background-color: #e3f2fd;` // Light blue background
}

const getYellowBackgroundStyle = () => {
  return `background-color: #fff9c4;` // Light yellow background
}

const getGreenBackgroundStyle = () => {
  return `background-color: #c8e6c9;` // Light green background
}

// Render function for columns that should only show data in last week of month
const renderLastWeekOnly = (
  value: any,
  formatter: (value: any) => string,
  data?: IAccountingSalesReportData,
) => {
  if (data?.isLastWeekOfMonth) {
    return formatter(value)
  }
  return ' '
}

const HideWhenNew = (
  content: string,
  data?: IAccountingSalesReportData,
  textWhenHide = '-',
) => {
  if (data?.showPastYearData) {
    return content
  }
  return textWhenHide
}

export default function useAccountingSalesReportColumns(
  year: number,
): IColumnsType<IAccountingSalesReportData>[] {
  const currentYear = year
  const previousYear = year - 1
  return useMemo(
    () => [
      {
        key: 'week',
        title: 'Week',
        align: 'center',
      },
      {
        key: 'formattedWeekendDate',
        title: 'Weekend Date',
        align: 'center',
        width: '150px',
      },
      {
        key: 'month',
        title: 'Month',
        align: 'center',
        render: (value: string, data?: IAccountingSalesReportData) =>
          renderLastWeekOnly(value, (v) => v, data),
        cellStyle: (_: string, data?: IAccountingSalesReportData) =>
          data?.isLastWeekOfMonth ? getBlueBackgroundStyle() : '',
      },
      {
        key: 'monthlySales',
        title: `Monthly Sales for ${currentYear}`,
        align: 'right',
        render: (value: number, data?: IAccountingSalesReportData) =>
          renderLastWeekOnly(
            value,
            (value: number) => {
              return value === 0 ? '-' : formatCurrency(value)
            },
            data,
          ),
        cellStyle: (_: number, data?: IAccountingSalesReportData) =>
          data?.isLastWeekOfMonth ? getBlueBackgroundStyle() : '',
      },
      {
        key: 'actualSales',
        title: `Actual ${currentYear} Net Sales`,
        align: 'right',
        render: (value: number) => {
          return value === 0 ? '-' : formatCurrency(value)
        },
      },
      {
        key: 'percentLW',
        title: '% LW',
        align: 'right',
        render: (value: number) => {
          return value === 0 ? ' ' : formatPercentage(value)
        },
      },
      {
        key: 'monthlySalesPastYear',
        title: `Monthly Sales for ${previousYear}`,
        align: 'right',
        render: (value: number, data?: IAccountingSalesReportData) =>
          renderLastWeekOnly(
            value,
            (value: number) => {
              return HideWhenNew(formatCurrency(value), data)
            },
            data,
          ),
        cellStyle: (_: number, data?: IAccountingSalesReportData) =>
          data?.isLastWeekOfMonth ? getBlueBackgroundStyle() : '',
      },
      {
        key: 'actualSalesPastYear',
        title: `Actual ${previousYear} Net Sales`,
        align: 'right',
        render: (value: number, data?: IAccountingSalesReportData) =>
          HideWhenNew(formatCurrency(value), data),
      },
      {
        key: 'compPercentLY',
        title: 'Comp % LY',
        align: 'right',
        render: (value: number, data?: IAccountingSalesReportData) =>
          HideWhenNew(formatPercentage(value), data, '0.00%'),
      },
      {
        key: 'budgetedSales',
        title: `Budgeted Sales for ${currentYear}`,
        align: 'right',
        render: (value: number) => {
          // Show "-" for future weeks (when budgetedSales is 0)
          if (value === 0) return '-'
          return formatCurrency(value)
        },
      },
      {
        key: 'cumulativeSales',
        title: `Cumulative Sales for ${currentYear}`,
        align: 'right',
        render: formatCurrency,
        cellStyle: (_: number, data?: IAccountingSalesReportData) =>
          data?.isLastWeekOfMonth ? getBlueBackgroundStyle() : '',
      },
      {
        key: 'cumulativeSalesPastYear',
        title: `Cumulative Sales for ${previousYear}`,
        align: 'right',
        render: (value: number, data?: IAccountingSalesReportData) =>
          HideWhenNew(formatCurrency(value), data),
        cellStyle: (_: number, data?: IAccountingSalesReportData) =>
          data?.isLastWeekOfMonth ? getBlueBackgroundStyle() : '',
      },
      {
        key: 'ytdCompLYMonthly',
        title: `Monthly Comp % LY`,
        align: 'right',
        render: (value: number, data?: IAccountingSalesReportData) =>
          renderLastWeekOnly(
            value,
            (value: number) => {
              return HideWhenNew(formatPercentage(value), data, '0.00%')
            },
            data,
          ),
        cellStyle: (_: number, data?: IAccountingSalesReportData) =>
          data?.isLastWeekOfMonth ? getYellowBackgroundStyle() : '',
      },
      {
        key: 'ytdCompLYCumulative',
        title: `YTD Comp % LY\n(Cumulative)`,
        align: 'right',
        render: (value: number, data?: IAccountingSalesReportData) =>
          renderLastWeekOnly(
            value,
            (value: number) => {
              return HideWhenNew(formatPercentage(value), data, '0.00%')
            },
            data,
          ),
        cellStyle: (_: number, data?: IAccountingSalesReportData) =>
          data?.isLastWeekOfMonth ? getGreenBackgroundStyle() : '',
      },
    ],
    [year],
  )
}
